import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combina múltiples clases de Tailwind CSS de forma eficiente
 * Resuelve conflictos automáticamente usando tailwind-merge
 * 
 * @example
 * ```tsx
 * cn("px-4 py-2", "bg-primary", condition && "text-white")
 * ```
 * 
 * @param inputs - Clases a combinar
 * @returns String con las clases combinadas
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formatea números como moneda
 * @param amount - Cantidad a formatear
 * @param currency - Código de moneda (default: 'EUR')
 * @returns String formateado como moneda
 * 
 * @example
 * ```tsx
 * formatCurrency(1234.56) // "1.234,56 €"
 * formatCurrency(1234.56, 'USD') // "$1,234.56"
 * ```
 */
export function formatCurrency(amount: number, currency: string = 'EUR'): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Formatea fechas de forma legible
 * @param date - Fecha a formatear
 * @returns String con la fecha formateada
 * 
 * @example
 * ```tsx
 * formatDate(new Date()) // "15 de enero de 2026"
 * formatDate("2026-01-15") // "15 de enero de 2026"
 * ```
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

/**
 * Formatea fechas de forma corta
 * @param date - Fecha a formatear
 * @returns String con la fecha formateada (dd/mm/yyyy)
 * 
 * @example
 * ```tsx
 * formatDateShort(new Date()) // "15/01/2026"
 * ```
 */
export function formatDateShort(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Trunca un texto a un número máximo de caracteres
 * @param text - Texto a truncar
 * @param maxLength - Longitud máxima
 * @param suffix - Sufijo a agregar (default: '...')
 * @returns Texto truncado
 * 
 * @example
 * ```tsx
 * truncateText("Este es un texto largo", 10) // "Este es un..."
 * ```
 */
export function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
}

/**
 * Copia texto al portapapeles de manera compatible con Office Add-ins y Edge.
 * Utiliza navigator.clipboard si es posible, y hace fallback a document.execCommand.
 * 
 * @param text - Texto a copiar
 * @returns Promise<boolean> true si se copió correctamente
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  try {
    // 1. Intento moderno (Chrome, Firefox, Edge actualizado en contexto seguro)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    console.warn('Clipboard API failed, trying fallback...', err);
  }

  // 2. Fallback robusto (Office Add-ins, iframes, Edge legacy)
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // Asegurar que el elemento no sea visible pero sea parte del DOM
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "0";
    textArea.setAttribute('readonly', ''); // Evitar teclado en móviles
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (err) {
    console.error('All copy methods failed:', err);
    return false;
  }
}

/**
 * Formatea bytes a un tamaño legible
 * @param bytes - Bytes a formatear
 * @param decimals - Número de decimales (default: 2)
 * @returns String formateado
 * 
 * @example
 * ```tsx
 * formatBytes(1024) // "1 KB"
 * formatBytes(1048576) // "1 MB"
 * ```
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Genera un ID único simple
 * @returns String con un ID único
 * 
 * @example
 * ```tsx
 * const id = generateId(); // "abc123def456"
 * ```
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Debounce function para optimizar llamadas
 * @param func - Función a ejecutar
 * @param wait - Tiempo de espera en ms
 * @returns Función debounced
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Normaliza un texto eliminando acentos y convirtiendo a minúsculas
 * Útil para búsquedas insensibles a mayúsculas y acentos
 * @param text - Texto a normalizar
 * @returns Texto normalizado sin acentos y en minúsculas
 * 
 * @example
 * ```tsx
 * normalizeText("José María") // "jose maria"
 * normalizeText("ÁÉÍÓÚ") // "aeiou"
 * ```
 */
export function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Maps common document mime types to the file extension used in storage paths
 * (documents bucket: `{id}/document{ext}`). Aligns with web app behavior.
 */
export function getDocumentStorageExtension(mimeType: string | null | undefined): string {
  if (!mimeType) return '.pdf';
  const m = mimeType.toLowerCase();
  if (m.includes('wordprocessingml')) return '.docx';
  if (m.includes('.google-apps.document')) return '.docx';
  if (m.includes('spreadsheet')) return '.xlsx';
  return '.pdf';
}















